// components/slides/slidesStore.ts
//
// Slides module store — the client-side document store for slide decks,
// mirroring the Analyst pattern (components/tables/tablesStore.ts): a module
// zustand store that command handlers reach via useSlidesStore.getState().
// Unlike Analyst tables, decks are DB-backed (SlideDeck rows via
// /api/slides/deck) because reviews live across weeks and devices; this store
// is the live working copy with a debounced save pipeline.
//
// Contracts:
// - Deck documents are SlideDeckDoc (types/types.ts): pages + overlay elements
//   in PAGE-POINT coordinates + the 10-point review state.
// - All mutations go through applyDocMutation (bumps version, queues save).
// - Per-deck UI runtime (active page, tool, zoom/pan, selection) lives in
//   `runtime` and is never persisted.

import { create } from 'zustand';
import { SLIDES_REVIEW_EVIDENCE_ENABLED, SLIDES_REVIEW_EVIDENCE_QA_PROJECT_ID } from '#lib/slides/reviewEvidence';

/** The element types a review PLACES - what 'Remove all review' deletes and
 *  what the launcher counts. One definition, importable without dragging any
 *  component code: the launcher and this store each carried a literal copy. */
export const REVIEW_PLACED_TYPES = new Set(['note', 'stamp']);
import {
  emptyHistory,
  pushHistory,
  redoHistory,
  undoHistory,
  type DeckHistory,
} from '#lib/slides/history';
import type {
  DeckReviewState,
  SlideCorrectionRequest,
  SlideDecisionRequest,
  SlideReviewComparison,
  SlideBrandResearchRun,
  SlideReviewRun,
  SlideAgentRun,
  SlideProposalRun,
  SlideProposalProgress,
  SlideDeckDTO,
  SlideDeckSummaryDTO,
  SlideDeckDoc,
  SlideDeckRuntimeState,
  SlideBlockKind,
  SlideElement,
  SlidePageDoc,
  SlidesGuidelineKey,
  useAgentsReturnType,
} from '#types/types';
import { reviewHasCompleteResult, proposalHasCompleteResult, REVIEW_PROMPT, BRAND_RESEARCH_PROMPT, resumeReviewPrompt, reviewLanguageFromPreference, reviewProgress, typedReviewIntent } from '#lib/slides/reviewStatus';
import { needsBrandResearch } from '#lib/slides/brandResearch';
import { resolveAuthorityStatus } from '#lib/slides/validationPoints';
import { BLOCK_LIBRARY, COVER_LAYOUT, COVER_PAGE, initialsOf, type BlockContext } from '#lib/slides/blockArtwork';

/** "PAGE : 1/12" follows the page count while it still reads like that; a reworded cell is left alone. */
const syncCartouchePageCount = (doc: SlideDeckDoc) => {
  for (const page of doc.pages) for (const el of page.elements) {
    if (el.type !== 'block' || el.block !== 'cartouche') continue;
    const auto = /^(\s*PAGE\s*:\s*1\s*\/\s*)\d+(\s*)$/i.exec(el.fields.page ?? '');
    if (auto) el.fields = { ...el.fields, page: `${auto[1]}${doc.pages.length}${auto[2]}` };
  }
};

/** What a library stamp is pre-filled with: the deck's own header, round and size. */
const blockContext = (deck: SlideDeckDTO, picked?: 'en' | 'fr', reviewer = ''): BlockContext => ({
  language: deck.doc.review?.language ?? picked, deckName: deck.name, brand: deck.doc.brandResearch?.brand.name, round: deck.reviewRound,
  // The RDM is whoever reviews: their initials until the stamp says otherwise (Thibaut, 2026-10-05).
  header: { ...deck.doc.review?.header, rdm: deck.doc.review?.header?.rdm?.trim() || reviewer }, pageCount: deck.doc.pages.length + (deck.doc.pages.some(p => p.cover) ? 0 : 1),
});

export const slidesGenId = (): string =>
  (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
    ? crypto.randomUUID()
    : `sl-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`);

const SAVE_DEBOUNCE_MS = 1200;
/** No character written for this long means nothing is driving the animation
 *  (headless run, deck closed) — give up rather than hold the command. */
const TYPING_STALL_MS = 4000;
const saveTimers: Record<string, ReturnType<typeof setTimeout>> = {};

interface SlidesSaveState {
  status: 'idle' | 'saving' | 'error';
  lastSavedAt?: number;
  savedVersion?: number;
  epoch?: number;
  conflict?: boolean;
  unconfirmed?: SlideDeckDTO;
  error?: string;
}

interface SlidesStoreState {
  /** Which face of the dashboard is showing: the deck cards, or the training table of corrections. */
  dashboardView: 'decks' | 'training';
  setDashboardView: (view: 'decks' | 'training') => void;
  /** Training shows the corrections table or the Reviewer's doctrine. */
  /** `projectId`: the dossier the Training queue is focused on, read in the order of its edits. */
  trainingExplorer: { view: 'rows' | 'files'; projectId?: string };
  patchTrainingExplorer: (patch: Partial<SlidesStoreState['trainingExplorer']>) => void;
  /** A deep link's page and element, applied once the deck it names has loaded. */
  pendingFocus: { projectId: string; pageId: string | null; elementId: string | null } | null;
  setPendingFocus: (focus: SlidesStoreState['pendingFocus']) => void;
  loadLearning: (projectId: string, userId: string, isCurrentActor: () => boolean, cursor?: string) => Promise<boolean>;
  compareLearning: (projectId: string, userId: string, isCurrentActor: () => boolean) => Promise<boolean>;
  submitLearning: (projectId: string, userId: string, isCurrentActor: () => boolean, request?: SlideCorrectionRequest | SlideDecisionRequest) => Promise<boolean>;
  exportLearning: (projectId: string, userId: string, isCurrentActor: () => boolean) => Promise<boolean>;
  /** Records an expert's decision on a Training row and returns what was stored (approve may land held out). */
  decideTraining: (input: { projectId: string; runId: string; changeId: string; decision: 'approved' | 'rejected' | 'pending' }) => Promise<'pending' | 'approved' | 'rejected' | 'held-out'>;
  /** Runs the pre-judge over the rows nobody judged yet; resolves to how many got a verdict. */
  judgeTraining: () => Promise<number>;
  /** When this session last asked for the pre-judge; the Training view asks once per session. */
  trainingJudgedAt: string | null;
  beginReviewRun: (projectId: string, sessionId: string, restart?: boolean, brief?: string) => SlideReviewRun | null;
  /** Synchronous store writes only; never keep an agent context across an await. */
  withAgentMutation: <T>(mutate: () => T, isAgentGenerated?: boolean) => T;
  getReviewRun: (sessionId: string) => SlideReviewRun | null;
  finishReviewRun: (run: SlideReviewRun | null, outcome: Exclude<SlideReviewRun['status'], 'running'>) => Promise<boolean>;
  getAgentRun: (sessionId: string) => SlideAgentRun | null;
  finishAgentRun: (run: SlideAgentRun | null, outcome: Exclude<SlideReviewRun['status'], 'running'>) => Promise<boolean>;
  getProposalRun: (sessionId: string) => SlideProposalRun | null;
  beginProposalRun: (projectId: string, sessionId: string, parentReviewRunId?: string) => SlideProposalRun | null;
  updateProposalProgress: (run: SlideProposalRun, progress: SlideProposalProgress) => void;
  recordProposalSheets: (run: SlideProposalRun, sheets: NonNullable<SlideProposalRun['sheets']>, needsInspection: boolean) => void;
  finishProposalRun: (run: SlideProposalRun, outcome: Exclude<SlideReviewRun['status'], 'running'>) => Promise<boolean>;
  welcomeUserId: string | null;
  tour: { userId: string; projectId: string | null; sessionId: string | null; step: number; learningRevealed?: boolean } | null;
  brandResearchRun: SlideBrandResearchRun | null;
  beginBrandResearch: (projectId: string, sessionId: string, thenReview?: SlideBrandResearchRun['thenReview']) => void;
  cancelBrandResearch: () => void;
  /** At the end of a session's turn or script chain: closes its finished research run and returns the review it was opened for. */
  takeReviewAfterResearch: (sessionId: string) => SlideBrandResearchRun['thenReview'] | null;
  /** A review the agent was asked for in the chat, started like the button once its turn ends. */
  pendingReviewLaunch: { sessionId: string; projectId: string } | null;
  patchBrandResearch: (id: string, patch: Partial<SlideBrandResearchRun>) => void;

  /** Loaded decks by projectId (the deck's container Project). */
  decks: Record<string, SlideDeckDTO>;
  /** File-browser index: every deck the user may open (server-listed). */
  deckIndex: SlideDeckSummaryDTO[];
  deckIndexLoaded: boolean;
  deckIndexLoading: boolean;
  deckIndexError: string | null;
  /** Per-deck UI runtime — never persisted. */
  runtime: Record<string, SlideDeckRuntimeState>;
  /** The deck open in the Slides editor (null = dashboard). */
  activeProjectId: string | null;
  saveStates: Record<string, SlidesSaveState>;
  /** Page ids whose snapshot the agent asked to view on its next pass (SLIDES_READ_PAGE). */
  visionRequestPageIds: string[];
  /** Timestamp driving the "agent working" glow on the canvas. */
  agentActiveAt: number | null;
  /** The annotation the Reviewer just placed/edited — drives the Fater agent
   *  cursor on the canvas (the Analyst's follow-the-agent UX, adapted). */
  agentEdits: SlidesAgentEdit | null;
  /** When ON (default), the editor jumps to the page the agent is annotating
   *  and the cursor glides to each new callout. */
  autoFollowAgent: boolean;
  /**
   * Callouts the agent has just placed, being typed out one character at a
   * time, in the order it wrote them.
   *
   * A review that appears as a finished block of bubbles reads as a machine
   * dump; watching a comment get written reads as someone reviewing. `order` is
   * the queue (only the head types, so bubbles land one after another rather
   * than all at once) and `shown` is how many characters of each are visible.
   * A note in `order` with shown === 0 has not been reached yet and is not
   * rendered at all — that is what makes each bubble appear in turn.
   */
  agentTyping: { order: string[]; shown: Record<string, number> };
  /**
   * Undo/redo stacks, per deck.
   *
   * Whole-document snapshots rather than per-action inverses: there is exactly
   * one place a doc is replaced, so recording it there makes every action
   * undoable, including ones written later and ones the agent performs. See
   * lib/slides/history.ts.
   */
  history: Record<string, DeckHistory<SlideDeckDoc>>;
  /**
   * The undo step currently open on behalf of the agent, so one command folds
   * into one entry. Refreshed by markAgentActive at the head of every command.
   *
   * A user action that lands within the coalescing window of an agent mutation
   * and carries no key of its own would join that step. In practice user actions
   * arrive seconds later, and the ones fired in bursts (element edits) derive
   * their own key, so this is a rounding error against the alternative of an
   * agent burst filling the stack.
   */
  agentStepKey: string | null;

  setActiveProject: (projectId: string | null) => void;
  loadDeckIndex: (options?: { refresh?: boolean }) => Promise<void>;
  /** Delete a deck (its SlideDeck + Project). Optimistic; reloads the index on failure. */
  deleteDeck: (projectId: string) => Promise<boolean>;
  closeDeck: () => void;
  ingestDeck: (deck: SlideDeckDTO) => void;
  loadDeck: (projectId: string, reload?: boolean) => Promise<SlideDeckDTO | null>;
  /** Core mutation path: clones+mutates the doc, bumps version, queues a debounced save.
   *
   *  Also the ONE place undo is recorded, which is why it is worth funnelling
   *  every doc change through here: a new action is undoable without its author
   *  writing a line of undo code.
   *
   *  `coalesceKey` names the gesture. Two mutations sharing a key in quick
   *  succession are one undo step - that is what stops an opacity slider from
   *  filling the stack with sixty entries. Leave it out and the mutation is its
   *  own step, which is the right default. */
  applyDocMutation: (
    projectId: string,
    mutate: (doc: SlideDeckDoc) => SlideDeckDoc,
    coalesceKey?: string
  ) => void;
  /** Step the deck back/forward one entry. No-ops (and saves nothing) at either end. */
  undo: (projectId: string) => void;
  redo: (projectId: string) => void;
  /** Whether there is anything to step to, for the toolbar's disabled state. */
  canUndo: (projectId: string) => boolean;
  canRedo: (projectId: string) => boolean;
  saveDeckNow: (projectId: string, snapshot?: SlideDeckDTO, isCurrentActor?: () => boolean) => Promise<boolean>;

  addElement: (projectId: string, pageId: string, element: SlideElement) => void;
  updateElement: (
    projectId: string,
    pageId: string,
    elementId: string,
    patch: Partial<SlideElement>,
    options?: { coalesce?: boolean }
  ) => void;
  removeElement: (projectId: string, pageId: string, elementId: string) => void;
  /** Remove several elements in one mutation (round-2 cleanups). Returns the count removed. */
  removeElements: (projectId: string, pageId: string, elementIds: string[]) => number;
  /** Copy an element on the same page, offset slightly, with a fresh id. */
  duplicateElement: (projectId: string, pageId: string, elementId: string) => SlideElement | null;
  /** Paint order is array order. Moves an element within its page's array. */
  reorderElement: (
    projectId: string,
    pageId: string,
    elementId: string,
    to: 'front' | 'back' | 'forward' | 'backward'
  ) => void;
  setPageLabel: (projectId: string, pageId: string, label: string) => void;
  /** Patch page-level properties (size, background, label) in one go. */
  patchPage: (
    projectId: string,
    pageId: string,
    patch: Partial<Pick<SlidePageDoc, 'label' | 'width' | 'height' | 'background'>>
  ) => void;
  /** Deep-copy a page (fresh element ids) and insert it after the original. */
  duplicatePage: (projectId: string, pageId: string) => SlidePageDoc | null;
  /** Reorder a page (drag in the rail / grid), renumbering all indices. */
  movePage: (projectId: string, pageId: string, toIndex: number) => void;
  /** Insert a page. Appends unless `atIndex` is given (0-based). */
  addPage: (projectId: string, page?: Partial<SlidePageDoc>, atIndex?: number) => SlidePageDoc | null;
  /** Puts Thibaut's review cover page in front of the tenant's deck, once: the
   *  validation stamp, Better Places, Tenant Survey and the Westfield cartouche. */
  ensureCoverPage: (projectId: string, reviewerInitials?: string) => void;
  /** Places a stamp from the RDM library on a page and selects it. */
  addLibraryStamp: (projectId: string, pageId: string, kind: SlideBlockKind, reviewerInitials?: string) => void;
  /** Delete a page (agent-driven); renumbers indices and refocuses a neighbour.
   *  Never removes the last remaining page. */
  deletePage: (projectId: string, pageId: string) => void;
  setReview: (projectId: string, patch: Partial<DeckReviewState>, coalesceKey?: string) => void;
  setReviewStatus: (projectId: string, status: NonNullable<DeckReviewState['status']>) => void;
  toggleAuthorityStatus: (projectId: string) => void;
  setDeckMeta: (
    projectId: string,
    patch: Partial<Pick<SlideDeckDTO, 'guidelineKey' | 'status' | 'reviewRound' | 'sourceFileName'>>
  ) => void;
  /**
   * Wipe the review: every callout and stamp element on every page, and the
   * verdict card back to empty. Returns how many elements were removed.
   *
   * Not expressible with setReview, which MERGES points so the agent can set p1
   * this turn and p3 the next — passing `points: {}` there merges nothing and
   * silently leaves all ten verdicts in place, which is exactly how the first
   * version of the "Remove all review" button did nothing at all. One mutation
   * so it is one undo step and one save, rather than a save per page.
   */
  clearReview: (projectId: string) => number;

  patchRuntime: (projectId: string, patch: Partial<SlideDeckRuntimeState>) => void;
  focusPage: (projectId: string, pageId: string) => void;
  requestVision: (pageId: string) => void;
  /** Drains the pending vision requests (called by the agent turn assembler). */
  consumeVisionRequests: () => string[];
  markAgentActive: () => void;
  setAgentEdits: (e: SlidesAgentEdit | null) => void;
  /** Queue a just-placed callout to be typed out. */
  startAgentTyping: (elementId: string) => void;
  /** Reveal `chars` of the queue head; drop it when it is fully written. */
  advanceAgentTyping: (elementId: string, chars: number, done: boolean) => void;
  /** Abandon the animation and show everything (page switch, reduced motion). */
  clearAgentTyping: () => void;
  /**
   * Resolve once every queued callout has been written out (or after `timeoutMs`).
   *
   * The command layer AWAITS this. Without it the agent placed its notes and
   * immediately moved to the next page, so the writing animation played to an
   * empty room — or never played at all, because a queued note renders as
   * nothing until its turn and the page had already changed. Pacing the script
   * to the animation is the whole point: the reviewer is meant to watch the
   * comment being written, not find it afterwards.
   */
  waitForAgentTyping: (timeoutMs?: number) => Promise<void>;
  /** The page the agent is currently reading, for the "being read" pulse. */
  readingPageId: string | null;
  setReadingPage: (pageId: string | null) => void;
  clearAgentEdits: () => void;
  setAutoFollowAgent: (on: boolean) => void;
}

/** The element the Reviewer agent just placed/edited (page points). */
interface SlidesAgentEdit {
  projectId: string;
  pageId: string;
  frame: { x: number; y: number; w: number; h: number };
  elementId?: string;
  at: number;
}

// The Training actions of the store: one request shape, one failure shape.
const postTraining = async <T,>(body: Record<string, unknown>, timeoutMs: number, failure: string): Promise<T> => {
  const response = await fetch('/api/slides/training', { method: 'POST', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body), signal: AbortSignal.timeout(timeoutMs) });
  const data = await response.json().catch(() => ({})) as { message?: unknown };
  if (!response.ok) throw new Error(typeof data.message === 'string' ? data.message : failure);
  return data as T;
};

export const useSlidesStore = create<SlidesStoreState>((set, get) => {
  let agentMutationDepth = 0;
  let deckIndexRequest: Promise<void> | null = null;
  const saveRequests = new Map<string, Promise<boolean>>();
  const queueSave = (projectId: string) => {
    if (saveTimers[projectId]) clearTimeout(saveTimers[projectId]);
    saveTimers[projectId] = setTimeout(() => {
      delete saveTimers[projectId];
      // Coalesce automatic saves while a request is in flight. Explicit export
      // snapshots still queue in order, without retaining every intermediate edit.
      if (saveRequests.has(projectId)) queueSave(projectId);
      else void get().saveDeckNow(projectId);
    }, SAVE_DEBOUNCE_MS);
  };
  /**
   * Undo and redo are the same move in opposite directions, so they share this.
   * Private to the store: the two directions are the API, and a caller able to
   * pass its own step function could put the stacks in a state neither one can
   * reach.
   */
  const stepHistory = (
    projectId: string,
    step: typeof undoHistory<SlideDeckDoc>
  ) => {
    const deck = get().decks[projectId];
    const history = get().history[projectId];
    if (!deck || !history) return;
    const stepped = step(history, deck.doc);
    if (!stepped) return; // Nothing to step to: leave the deck and its save alone.
    set((s) => ({
      decks: {
        ...s.decks,
        // Undo changes the document, not whether its execution is still running.
        [projectId]: { ...deck, doc: { ...stepped.doc, reviewRun: deck.doc.reviewRun?.status === 'running'
          ? { ...deck.doc.reviewRun, userEdited: true } : deck.doc.reviewRun,
          proposalRun: deck.doc.proposalRun }, version: deck.version + 1 },
      },
      history: { ...s.history, [projectId]: stepped.history },
      // An element that no longer exists must not stay selected: the sidebar
      // would render a panel for it and patch a ghost back into the document.
      runtime: {
        ...s.runtime,
        [projectId]: {
          ...s.runtime[projectId],
          selectedElementIds: (s.runtime[projectId]?.selectedElementIds ?? []).filter((id) =>
            stepped.doc.pages.some((p) => p.elements.some((e) => e.id === id))
          ),
        },
      },
    }));
    queueSave(projectId);
  };

  return {
  withAgentMutation: (mutate, isAgentGenerated = true) => {
    if (!isAgentGenerated) return mutate();
    agentMutationDepth++;
    try { return mutate(); } finally { agentMutationDepth--; }
  },
  brandResearchRun: null,
  getAgentRun: (sessionId) => get().getReviewRun(sessionId) ?? get().getProposalRun(sessionId),
  finishAgentRun: (run, outcome) => run && 'kind' in run
    ? get().finishProposalRun(run, outcome) : get().finishReviewRun(run, outcome),
  getProposalRun: (sessionId) => {
    const state = get();
    return Object.values(state.decks).find(deck => {
      const run = deck.doc.proposalRun;
      return run?.sessionId === sessionId && (run.status === 'running' || run.status === 'completed') &&
        state.runtime[deck.projectId]?.proposalRunId === run.id && deck.reviewRound === run.round;
    })?.doc.proposalRun ?? null;
  },
  beginProposalRun: (projectId, sessionId, parentReviewRunId) => {
    const deck = get().decks[projectId];
    const parent = get().getReviewRun(sessionId);
    const insideReview = !!parent && parent.id === parentReviewRunId && parent.projectId === projectId;
    if (!deck || !sessionId || (!insideReview && get().getAgentRun(sessionId)) ||
        (deck.doc.reviewRun && get().getReviewRun(deck.doc.reviewRun.sessionId)?.id === deck.doc.reviewRun.id && !insideReview) ||
        (deck.doc.proposalRun && get().getProposalRun(deck.doc.proposalRun.sessionId)?.id === deck.doc.proposalRun.id)) return null;
    const run: SlideProposalRun = { kind: 'proposal', id: slidesGenId(), projectId, sessionId,
      round: deck.reviewRound, status: 'running', startedAt: new Date().toISOString() };
    set(s => ({
      decks: { ...s.decks, [projectId]: { ...deck, version: deck.version + 1, doc: { ...deck.doc, proposalRun: run } } },
      runtime: { ...s.runtime, [projectId]: { ...s.runtime[projectId], proposalRunId: run.id, proposalProgress: { phase: 'preparing' } } },
    }));
    queueSave(projectId);
    return run;
  },
  updateProposalProgress: (run, progress) => {
    if (get().getProposalRun(run.sessionId)?.id !== run.id) return;
    get().patchRuntime(run.projectId, { proposalProgress: progress });
  },
  recordProposalSheets: (run, sheets, needsInspection) => {
    const deck = get().decks[run.projectId];
    if (!deck || get().getProposalRun(run.sessionId)?.id !== run.id || deck.doc.proposalRun?.status !== 'running') return;
    set(s => ({ decks: { ...s.decks, [run.projectId]: { ...deck, version: deck.version + 1,
      doc: { ...deck.doc, proposalRun: { ...deck.doc.proposalRun!, sheets, needsInspection } } } } }));
    queueSave(run.projectId);
  },
  finishProposalRun: async (run, outcome) => {
    const deck = get().decks[run.projectId];
    if (!deck || get().getProposalRun(run.sessionId)?.id !== run.id) return false;
    // Stop still wins while a completed pair is waiting for its final save acknowledgement.
    if (deck.doc.proposalRun?.status !== 'running' && outcome !== 'stopped') return false;
    const status = outcome === 'completed' && !proposalHasCompleteResult(deck) ? 'incomplete' : outcome;
    const finished: SlideDeckDTO = { ...deck, version: deck.version + 1,
      doc: { ...deck.doc, proposalRun: { ...deck.doc.proposalRun!, status } } };
    set(s => ({
      decks: { ...s.decks, [run.projectId]: finished },
      runtime: { ...s.runtime, [run.projectId]: { ...s.runtime[run.projectId],
        proposalRunId: status === 'completed' ? run.id : null, proposalProgress: undefined } },
    }));
    try { return await get().saveDeckNow(run.projectId, finished); }
    finally {
      if (get().runtime[run.projectId]?.proposalRunId === run.id) get().patchRuntime(run.projectId, { proposalRunId: null });
    }
  },
  getReviewRun: (sessionId) => {
    const state = get();
    return Object.values(state.decks).find((deck) => {
      const run = deck.doc.reviewRun;
      return run?.sessionId === sessionId && run.status === 'running' &&
        state.runtime[deck.projectId]?.reviewRunId === run.id && deck.reviewRound === run.round;
    })?.doc.reviewRun ?? null;
  },
  beginReviewRun: (projectId, sessionId, restart = false, brief) => {
    const current = get().decks[projectId];
    if (!current || get().getAgentRun(sessionId) ||
        (current.doc.proposalRun && get().getProposalRun(current.doc.proposalRun.sessionId)?.id === current.doc.proposalRun.id) ||
        (current.doc.reviewRun?.status === 'running' && current.doc.reviewRun.round === current.reviewRound &&
          get().runtime[projectId]?.reviewRunId === current.doc.reviewRun.id)) return null;
    if (restart) get().clearReview(projectId);
    const deck = get().decks[projectId]!;
    const run: SlideReviewRun = {
      id: slidesGenId(), projectId, sessionId, round: deck.reviewRound,
      status: 'running', startedAt: new Date().toISOString(),
      ...(SLIDES_REVIEW_EVIDENCE_ENABLED && (!SLIDES_REVIEW_EVIDENCE_QA_PROJECT_ID || projectId === SLIDES_REVIEW_EVIDENCE_QA_PROJECT_ID)
        ? { evidenceVersion: 1 as const } : {}), brief, userEdited: false, agentMutationCount: 0,
    };
    const review = deck.doc.review ?? { guidelineKey: deck.guidelineKey, round: deck.reviewRound, points: {} };
    const points = review.points ?? {};
    const header = !Object.keys(points).length && review.header?.date === undefined
      ? { ...review.header, date: new Date().toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: '2-digit', timeZone: 'UTC' }) }
      : review.header;
    set((s) => ({
      decks: { ...s.decks, [projectId]: { ...deck, status: 'REVIEWING', version: deck.version + 1,
        doc: { ...deck.doc, review: { ...review, points, header }, reviewRun: run } } },
      runtime: { ...s.runtime, [projectId]: { ...s.runtime[projectId], reviewRunId: run.id } },
    }));
    queueSave(projectId);
    return run;
  },
  finishReviewRun: async (run, outcome) => {
    if (!run) return false;
    const deck = get().decks[run.projectId];
    if (!deck || deck.doc.reviewRun?.id !== run.id || deck.doc.reviewRun.status !== 'running' ||
        deck.reviewRound !== run.round || get().runtime[run.projectId]?.reviewRunId !== run.id) return false;
    const status = outcome === 'completed' && !reviewHasCompleteResult(deck) ? 'incomplete' : outcome;
    const version = deck.version + 1;
    const finished: SlideDeckDTO = { ...deck, version, status: status === 'completed' ? 'REVIEWED' : 'REVIEWING',
      doc: { ...deck.doc, reviewRun: { ...deck.doc.reviewRun, status } } };
    set((s) => ({
      decks: { ...s.decks, [run.projectId]: finished },
      runtime: { ...s.runtime, [run.projectId]: { ...s.runtime[run.projectId], reviewRunId: null } },
    }));
    return get().saveDeckNow(run.projectId, finished);
  },
  beginBrandResearch: (projectId, sessionId, thenReview) => {
    get().cancelBrandResearch();
    set({ brandResearchRun: { id: slidesGenId(), projectId, sessionId,
      controller: new AbortController(), phase: 'identify', searches: 0,
      webFailed: false, internalFailed: false, sources: [], references: [], thenReview } });
  },
  cancelBrandResearch: () => {
    const run = get().brandResearchRun;
    set({ brandResearchRun: null });
    run?.controller.abort();
  },
  takeReviewAfterResearch: (sessionId) => {
    const run = get().brandResearchRun;
    if (!run?.thenReview || run.sessionId !== sessionId || run.phase !== 'complete' || run.projectId !== get().activeProjectId) return null;
    get().cancelBrandResearch();
    return run.thenReview;
  },
  patchBrandResearch: (id, patch) => set((s) => ({
    brandResearchRun: s.brandResearchRun?.id === id ? { ...s.brandResearchRun, ...patch } : s.brandResearchRun,
  })),
  loadLearning: async (projectId, userId, isCurrentActor, cursor) => {
    if (!isCurrentActor()) return false;
    let state = get().runtime[projectId]?.learning;
    if (state?.loading && state.userId === userId) return false;
    if (state?.userId !== userId) state = { userId, loading: false, annotations: [], decisionReason: '',
      applicability: '', family: '', partition: 'reference', confirmed: false, lessons: {} };
    get().patchRuntime(projectId, { learning: { ...state, loading: true, error: undefined } });
    try {
      const response = await fetch(`/api/slides/deck?${new URLSearchParams({ projectId, learning: 'history', ...(cursor ? { learningCursor: cursor } : {}) })}`, { cache: 'no-store', signal: AbortSignal.timeout(30000) });
      const data = await response.json();
      if (!response.ok) throw new Error(data.message ?? 'Learning history could not be loaded.');
      const current = get().runtime[projectId]?.learning;
      if (current?.userId !== userId || !isCurrentActor()) throw new Error('The active account changed.');
      get().patchRuntime(projectId, { learning: { ...current, loading: false, view: data, historyCursor: cursor,
        selectedCheckpointId: undefined, confirmed: false } });
      return true;
    } catch (error) {
      const current = get().runtime[projectId]?.learning;
      if (current?.userId === userId) get().patchRuntime(projectId, { learning: { ...current, loading: false,
        error: error instanceof Error ? error.message : 'Learning history could not be loaded.' } });
      return false;
    }
  },

  exportLearning: async (projectId, userId, isCurrentActor) => {
    if (!isCurrentActor()) return false;
    const state = get().runtime[projectId]?.learning;
    if (!state || state.userId !== userId || state.loading) return false;
    get().patchRuntime(projectId, { learning: { ...state, loading: true, error: undefined } });
    try {
      const response = await fetch(`/api/slides/deck?${new URLSearchParams({ projectId, learning: 'export',
        ...(state.historyCursor ? { learningCursor: state.historyCursor } : {}) })}`,
      { cache: 'no-store', signal: AbortSignal.timeout(30000) });
      const data = await response.json();
      if (!response.ok) throw new Error(data.message ?? 'Learning evidence could not be exported.');
      const current = get().runtime[projectId]?.learning;
      if (current?.userId !== userId || !isCurrentActor()) throw new Error('The active account changed.');
      const url = URL.createObjectURL(new Blob([JSON.stringify({ projectId, scope: 'Current learning history page', ...data }, null, 2)], { type: 'application/json' }));
      try {
        const link = document.createElement('a'); link.href = url; link.download = 'slides-learning-evidence-page.json'; link.click();
      } finally { URL.revokeObjectURL(url); }
      get().patchRuntime(projectId, { learning: { ...current, loading: false } });
      return true;
    } catch (error) {
      const current = get().runtime[projectId]?.learning;
      if (current?.userId === userId) get().patchRuntime(projectId, { learning: { ...current, loading: false,
        error: error instanceof Error ? error.message : 'Learning evidence could not be exported.' } });
      return false;
    }
  },

  compareLearning: async (projectId, userId, isCurrentActor) => {
    if (!isCurrentActor()) return false;
    const state = get().runtime[projectId]?.learning;
    const baseline = state?.view?.baseline;
    if (!state || state.userId !== userId || state.loading || state.pending || !baseline) return false;
    get().patchRuntime(projectId, { learning: { ...state, loading: true, error: undefined } });
    try {
      if (!await get().saveDeckNow(projectId, undefined, isCurrentActor)) throw new Error('Save the deck before comparing. Your correction draft is kept.');
      if (!isCurrentActor()) throw new Error('The active account changed.');
      const response = await fetch(`/api/slides/deck?${new URLSearchParams({ projectId, learning: 'compare', baselineId: baseline.id, baselineHash: baseline.hash })}`, { cache: 'no-store', signal: AbortSignal.timeout(30000) });
      const data = await response.json();
      if (!response.ok) throw new Error(data.message ?? 'Review comparison failed.');
      const current = get().runtime[projectId]?.learning;
      if (current?.userId !== userId || !isCurrentActor()) throw new Error('The active account changed.');
      const comparison = data as SlideReviewComparison;
      const previousChanges = new Map(current.comparison?.changes.map(change => [change.id, change]));
      const nextChanges = new Map(comparison.changes.map(change => [change.id, change]));
      const sameBaseline = current.comparison?.baselineId === comparison.baselineId &&
        current.comparison.baselineHash === comparison.baselineHash;
      const annotations: SlideCorrectionRequest['annotations'] = [];
      const orphanAnnotations = [...(current.orphanAnnotations ?? [])];
      for (const annotation of current.annotations) {
        const previous = previousChanges.get(annotation.changeId);
        const next = nextChanges.get(annotation.changeId);
        if (sameBaseline && previous && next && JSON.stringify(previous) === JSON.stringify(next)) annotations.push(annotation);
        else orphanAnnotations.push({ annotation, change: previous });
      }
      get().patchRuntime(projectId, { learning: { ...current, loading: false, comparison,
        annotations, orphanAnnotations } });
      return true;
    } catch (error) {
      const current = get().runtime[projectId]?.learning;
      if (current?.userId === userId) get().patchRuntime(projectId, { learning: { ...current, loading: false,
        error: error instanceof Error ? error.message : 'Review comparison failed.' } });
      return false;
    }
  },

  submitLearning: async (projectId, userId, isCurrentActor, request) => {
    if (!isCurrentActor()) return false;
    const state = get().runtime[projectId]?.learning;
    if (!state || state.userId !== userId || state.loading) return false;
    const pending = state.pending ?? request;
    if (!pending || pending.projectId !== projectId) return false;
    get().patchRuntime(projectId, { learning: { ...state, pending, loading: true, error: undefined } });
    let rejected = false;
    try {
      const response = await fetch('/api/slides/deck', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(pending), signal: AbortSignal.timeout(30000) });
      rejected = response.status >= 400 && response.status < 500;
      const data = await response.json();
      if (!response.ok) throw new Error(data.message ?? 'The review decision was not acknowledged. Retry the same request.');
      const current = get().runtime[projectId]?.learning;
      if (current?.userId !== userId || !isCurrentActor()) throw new Error('The active account changed.');
      get().patchRuntime(projectId, { learning: { ...current, pending: undefined, loading: false,
        acknowledgedId: data.event.id, confirmed: false, ...(pending.action === 'checkpoint' ? { annotations: [] } : {}) } });
      await get().loadLearning(projectId, userId, isCurrentActor);
      return true;
    } catch (error) {
      const current = get().runtime[projectId]?.learning;
      if (current?.userId === userId) get().patchRuntime(projectId, { learning: { ...current, loading: false,
        ...(rejected ? { pending: undefined } : {}),
        error: error instanceof Error ? error.message : 'The review decision was not acknowledged. Retry the same request.' } });
      return false;
    }
  },

  decideTraining: (input) => postTraining<{ decision: 'pending' | 'approved' | 'rejected' | 'held-out' }>({ action: 'decide', ...input }, 30000, 'The decision was not saved.')
    .then(data => data.decision),
  trainingJudgedAt: null,
  judgeTraining: async () => {
    set({ trainingJudgedAt: new Date().toISOString() });
    const data = await postTraining<{ judged?: unknown }>({ action: 'judge' }, 120000, 'The new corrections could not be judged.');
    return typeof data.judged === 'number' ? data.judged : 0;
  },

  pendingReviewLaunch: null,
  dashboardView: 'decks',
  setDashboardView: (view) => set({ dashboardView: view }),
  trainingExplorer: { view: 'rows' },
  patchTrainingExplorer: (patch) => set((s) => ({ trainingExplorer: { ...s.trainingExplorer, ...patch } })),
  pendingFocus: null,
  setPendingFocus: (focus) => set({ pendingFocus: focus }),
  welcomeUserId: null,
  tour: null,
  decks: {},
  deckIndex: [],
  deckIndexLoaded: false,
  deckIndexLoading: false,
  deckIndexError: null,
  runtime: {},
  activeProjectId: null,
  saveStates: {},
  visionRequestPageIds: [],
  agentActiveAt: null,
  agentEdits: null,
  autoFollowAgent: true,
  agentTyping: { order: [], shown: {} },
  history: {},
  agentStepKey: null,
  readingPageId: null,

  setActiveProject: (projectId) => {
    if (get().brandResearchRun?.projectId !== projectId) get().cancelBrandResearch();
    set({ activeProjectId: projectId });
  },

  loadDeckIndex: (options) => {
    if (deckIndexRequest) {
      // Imports need a snapshot taken after the mutation, even if initial loading is still running.
      return options?.refresh
        ? deckIndexRequest.then(() => get().loadDeckIndex())
        : deckIndexRequest;
    }
    deckIndexRequest = Promise.resolve().then(async () => {
      set({ deckIndexLoading: true, deckIndexError: null });
      for (let attempt = 0; attempt < 3; attempt += 1) {
        let retryable = true;
        try {
          const res = await fetch('/api/slides/decks', { signal: AbortSignal.timeout(15_000) });
          if (!res.ok) {
            retryable = res.status === 408 || res.status === 429 || res.status >= 500;
            throw new Error(`Deck index returned ${res.status}`);
          }
          const data = (await res.json()) as { decks?: SlideDeckSummaryDTO[] };
          retryable = false;
          if (!Array.isArray(data.decks)) throw new Error('Invalid deck index response');
          set({ deckIndex: data.decks, deckIndexLoaded: true });
          return;
        } catch {
          if (!retryable || attempt === 2) {
            set({ deckIndexError: 'Could not load the deck list. Please try again.' });
            return;
          }
          await new Promise((resolve) => setTimeout(resolve, (attempt + 1) * 1000));
        }
      }
    }).finally(() => {
      deckIndexRequest = null;
      set({ deckIndexLoading: false });
    });
    return deckIndexRequest;
  },

  deleteDeck: async (projectId) => {
    // Optimistic remove from the index + the loaded cache; close it if open.
    // Snapshot the full slice we touch so a failed delete restores all of it
    // (not just the index — the cached doc and the open-deck pointer too).
    const prevIndex = get().deckIndex;
    const prevDecks = get().decks;
    const prevActive = get().activeProjectId;
    set((s) => {
      const decks = { ...s.decks };
      delete decks[projectId];
      return {
        deckIndex: s.deckIndex.filter((d) => d.projectId !== projectId),
        decks,
        activeProjectId: s.activeProjectId === projectId ? null : s.activeProjectId,
      };
    });
    try {
      const res = await fetch(`/api/slides/deck?projectId=${encodeURIComponent(projectId)}`, { method: 'DELETE' });
      if (!res.ok) throw new Error(`delete failed: ${res.status}`);
      return true;
    } catch (error) {
      console.error('[Slides] deleteDeck failed, restoring:', error);
      set({ deckIndex: prevIndex, decks: prevDecks, activeProjectId: prevActive }); // roll back the optimistic removal
      return false;
    }
  },

  closeDeck: () => get().setActiveProject(null),

  ingestDeck: (deck) =>
    set((s) => ({
      decks: { ...s.decks, [deck.projectId]: deck },
      saveStates: { ...s.saveStates, [deck.projectId]: {
        status: 'idle', savedVersion: deck.version,
        epoch: (s.saveStates[deck.projectId]?.epoch ?? 0) + 1,
      } },
      // The server's copy replaces ours, so the local history describes a
      // document lineage that no longer exists. Keeping it would let one undo
      // press overwrite whatever the other writer just saved.
      history: { ...s.history, [deck.projectId]: emptyHistory<SlideDeckDoc>() },
      runtime: {
        ...s.runtime,
        [deck.projectId]: {
          activePageId: deck.doc.pages[0]?.id,
          activeTool: 'select',
          rightBarTab: 'pages',
          ...s.runtime[deck.projectId],
          reviewRunId: deck.doc.reviewRun?.round === deck.reviewRound && deck.doc.reviewRun?.status === 'running' &&
            deck.doc.reviewRun?.id === s.runtime[deck.projectId]?.reviewRunId ? deck.doc.reviewRun.id : null,
          proposalRunId: deck.doc.proposalRun && deck.doc.proposalRun.round === deck.reviewRound &&
            deck.doc.proposalRun.id === s.runtime[deck.projectId]?.proposalRunId ? deck.doc.proposalRun.id : null,
        },
      },
    })),

  loadDeck: async (projectId, reload = false) => {
    const existing = get().decks[projectId];
    if (existing && !reload) return existing;
    try {
      const res = await fetch(`/api/slides/deck?projectId=${encodeURIComponent(projectId)}`, { signal: AbortSignal.timeout(30_000) });
      if (!res.ok) return null;
      const data = (await res.json()) as { deck?: SlideDeckDTO };
      if (!data.deck || data.deck.projectId !== projectId) return null;
      // Confirmation covers only the local document seen when reload started.
      if (reload && get().decks[projectId] !== existing) return null;
      get().ingestDeck(data.deck);
      return data.deck;
    } catch {
      return null;
    }
  },

  applyDocMutation: (projectId, mutate, coalesceKey) => {
    const deck = get().decks[projectId];
    if (!deck) return;
    // Mutate first. A mutate() that throws (every one of them rejects an unknown
    // page or element id) must not leave an undo entry for a change that never
    // happened.
    const nextDoc = mutate(structuredClone(deck.doc));
    if (nextDoc.pages.length !== deck.doc.pages.length) syncCartouchePageCount(nextDoc);
    const run = deck.doc.reviewRun;
    if (run?.status === 'running' && nextDoc.reviewRun) {
      nextDoc.reviewRun = agentMutationDepth > 0
        ? { ...run, agentMutationCount: (run.agentMutationCount ?? 0) + 1 }
        : { ...run, userEdited: true };
    } else if (run && nextDoc.reviewRun && agentMutationDepth > 0) {
      // After the review, the agent still writes (follow-up commands, the counter-proposal).
      // The count keeps growing so the server can tell those saves from the expert's edits.
      nextDoc.reviewRun = { ...run, agentMutationCount: (run.agentMutationCount ?? 0) + 1 };
    }
    set((s) => ({
      decks: {
        ...s.decks,
        [projectId]: { ...deck, doc: nextDoc, version: deck.version + 1 },
      },
      history: {
        ...s.history,
        [projectId]: pushHistory(
          s.history[projectId] ?? emptyHistory<SlideDeckDoc>(),
          deck.doc,
          coalesceKey ?? null,
          Date.now()
        ),
      },
    }));
    queueSave(projectId);
  },

  undo: (projectId) => stepHistory(projectId, undoHistory),
  redo: (projectId) => stepHistory(projectId, redoHistory),
  canUndo: (projectId) => (get().history[projectId]?.past.length ?? 0) > 0,
  canRedo: (projectId) => (get().history[projectId]?.future.length ?? 0) > 0,

  saveDeckNow: (projectId, snapshot, isCurrentActor) => {
    const deck = snapshot ?? get().decks[projectId];
    if (!deck || deck.projectId !== projectId || isCurrentActor?.() === false) return Promise.resolve(false);
    if (saveTimers[projectId]) clearTimeout(saveTimers[projectId]);
    delete saveTimers[projectId];
    const epoch = get().saveStates[projectId]?.epoch;
    const previous = saveRequests.get(projectId);
    const persist = async (candidate: SlideDeckDTO): Promise<boolean> => {
      if (isCurrentActor?.() === false) return false;
      const state = get().saveStates[projectId];
      if (!get().decks[projectId] || state?.epoch !== epoch || state?.conflict) return false;
      if (state?.savedVersion === candidate.version) return true;
      if ((state?.savedVersion ?? -1) > candidate.version) return false;
      const baseVersion = state?.savedVersion ?? candidate.version;
      let uncertain = true;
      set((s) => ({ saveStates: { ...s.saveStates, [projectId]: { ...state, status: 'saving', error: undefined } } }));
      try {
        const res = await fetch('/api/slides/deck', {
          method: 'PUT',
          signal: AbortSignal.timeout(30_000),
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            projectId, doc: candidate.doc, version: candidate.version, baseVersion,
            guidelineKey: candidate.guidelineKey, status: candidate.status, reviewRound: candidate.reviewRound,
          }),
        });
        // Explicit reload/import starts a new lineage; old responses must not
        // acknowledge, replace, or flag the newly loaded document.
        if (isCurrentActor?.() === false || get().saveStates[projectId]?.epoch !== epoch || !get().decks[projectId]) return false;
        if (res.status === 409) {
          set((s) => ({ saveStates: { ...s.saveStates, [projectId]: {
            ...state, status: 'error', conflict: true,
            error: 'This deck changed elsewhere. Your edits are kept in this tab; resolve the conflict before exporting.',
          } } }));
          return false;
        }
        if (!res.ok) {
          uncertain = res.status >= 500;
          throw new Error(`Could not save the deck (${res.status}). Your edits are kept; retry export.`);
        }
        const saved = (await res.json())?.deck;
        if (saved?.projectId !== projectId || saved.version !== candidate.version) {
          throw new Error('The save could not be confirmed. Your edits are kept; retry export.');
        }
        if (isCurrentActor?.() === false || get().saveStates[projectId]?.epoch !== epoch || !get().decks[projectId]) return false;
        set((s) => ({ saveStates: { ...s.saveStates, [projectId]: {
          ...state, status: 'idle', savedVersion: saved.version, lastSavedAt: Date.now(), error: undefined, unconfirmed: undefined,
        } } }));
        return true;
      } catch (error) {
        if (isCurrentActor?.() !== false && get().saveStates[projectId]?.epoch === epoch && get().decks[projectId]) {
          set((s) => ({ saveStates: { ...s.saveStates, [projectId]: {
            ...state, status: 'error', unconfirmed: uncertain ? candidate : undefined,
            error: error instanceof Error ? error.message : 'Could not save. Retry export.',
          } } }));
        }
        return false;
      }
    };
    const request = (async () => {
      await previous;
      const uncertain = get().saveStates[projectId]?.unconfirmed;
      // The previous write may have committed despite losing its response.
      // Re-acknowledge that exact revision before attempting newer local edits.
      if (uncertain && uncertain.version !== deck.version && !(await persist(uncertain))) return false;
      return persist(deck);
    })().finally(() => {
      if (saveRequests.get(projectId) === request) saveRequests.delete(projectId);
    });
    saveRequests.set(projectId, request);
    return request;
  },

  addElement: (projectId, pageId, element) =>
    get().applyDocMutation(
      projectId,
      (doc) => {
        const page = doc.pages.find((p) => p.id === pageId);
        if (!page) throw new Error(`Unknown page "${pageId}"`);
        // Provenance default: anything not explicitly tagged (the agent command
        // tags 'agent') was placed by a human tool. Phase-2 learning reads this.
        page.elements.push({ origin: 'user', ...element });
        return doc;
      },
      // The coalesce key rides the ELEMENT's provenance, never a sticky global:
      // an agent batch folds into one gesture under the turn's key, while a
      // user's quick edits stay their own steps. The old fallback (any unkeyed
      // mutation inherits agentStepKey forever) folded a user's two quick edits
      // into one Ctrl+Z hours after the agent last ran.
      element.origin === 'agent' ? get().agentStepKey ?? undefined : undefined
    ),

  updateElement: (projectId, pageId, elementId, patch, options) =>
    get().applyDocMutation(
      projectId,
      (doc) => {
        const page = doc.pages.find((p) => p.id === pageId);
        if (!page) throw new Error(`Unknown page "${pageId}"`);
        const idx = page.elements.findIndex((e) => e.id === elementId);
        if (idx < 0) throw new Error(`Unknown element "${elementId}" on page "${pageId}"`);
        const next = { ...page.elements[idx], ...patch, id: elementId } as SlideElement;
        // The Reviewer rewriting a callout makes the words its own again.
        if (agentMutationDepth > 0 && 'text' in patch) delete next.writtenBy;
        page.elements[idx] = next;
        return doc;
      },
      // Sliders stream changes; canvas blur/pointerup commits are already whole
      // gestures and must remain separate even when they happen within1200ms.
      options?.coalesce === false ? undefined : `update:${elementId}:${Object.keys(patch).sort().join(',')}`
    ),

  removeElement: (projectId, pageId, elementId) =>
    get().applyDocMutation(projectId, (doc) => {
      const page = doc.pages.find((p) => p.id === pageId);
      if (!page) throw new Error(`Unknown page "${pageId}"`);
      page.elements = page.elements.filter((e) => e.id !== elementId);
      return doc;
    }),

  removeElements: (projectId, pageId, elementIds) => {
    const ids = new Set(elementIds);
    if (ids.size === 0) return 0;
    const deck = get().decks[projectId];
    const before = deck?.doc.pages.find((p) => p.id === pageId)?.elements.length ?? 0;
    let after = before;
    get().applyDocMutation(projectId, (doc) => {
      const page = doc.pages.find((p) => p.id === pageId);
      if (!page) throw new Error(`Unknown page "${pageId}"`);
      page.elements = page.elements.filter((e) => !ids.has(e.id));
      after = page.elements.length;
      return doc;
    });
    return before - after;
  },

  duplicateElement: (projectId, pageId, elementId) => {
    const deck = get().decks[projectId];
    const source = deck?.doc.pages.find((p) => p.id === pageId)?.elements.find((e) => e.id === elementId);
    if (!source) return null;
    // Offset so the copy is visibly distinct rather than pixel-perfectly hidden
    // behind the original.
    const copy = {
      ...structuredClone(source),
      id: slidesGenId(),
      frame: { ...source.frame, x: source.frame.x + 12, y: source.frame.y + 12 },
    } as SlideElement;
    get().addElement(projectId, pageId, copy);
    return copy;
  },

  reorderElement: (projectId, pageId, elementId, to) =>
    get().applyDocMutation(projectId, (doc) => {
      const page = doc.pages.find((p) => p.id === pageId);
      if (!page) throw new Error(`Unknown page "${pageId}"`);
      const from = page.elements.findIndex((e) => e.id === elementId);
      if (from < 0) throw new Error(`Unknown element "${elementId}" on page "${pageId}"`);
      const [el] = page.elements.splice(from, 1);
      if (!el) return doc;
      const last = page.elements.length;
      const target =
        to === 'front' ? last
        : to === 'back' ? 0
        : to === 'forward' ? Math.min(last, from + 1)
        : Math.max(0, from - 1);
      page.elements.splice(target, 0, el);
      return doc;
    }),

  setPageLabel: (projectId, pageId, label) =>
    get().applyDocMutation(projectId, (doc) => {
      const page = doc.pages.find((p) => p.id === pageId);
      if (!page) throw new Error(`Unknown page "${pageId}"`);
      page.label = label;
      return doc;
    }),

  patchPage: (projectId, pageId, patch) =>
    get().applyDocMutation(projectId, (doc) => {
      const page = doc.pages.find((p) => p.id === pageId);
      if (!page) throw new Error(`Unknown page "${pageId}"`);
      if (patch.label !== undefined) page.label = patch.label;
      if (patch.background !== undefined) page.background = patch.background;
      if (typeof patch.width === 'number' && patch.width > 0) page.width = patch.width;
      if (typeof patch.height === 'number' && patch.height > 0) page.height = patch.height;
      return doc;
    }),

  duplicatePage: (projectId, pageId) => {
    const deck = get().decks[projectId];
    const source = deck?.doc.pages.find((p) => p.id === pageId);
    if (!source) return null;
    const copy: SlidePageDoc = {
      ...structuredClone(source),
      id: slidesGenId(),
      label: source.label ? `${source.label} (copy)` : undefined,
      elements: source.elements.map((e) => ({ ...structuredClone(e), id: slidesGenId() })),
    };
    get().applyDocMutation(projectId, (doc) => {
      const at = doc.pages.findIndex((p) => p.id === pageId);
      doc.pages.splice(at + 1, 0, copy);
      doc.pages.forEach((p, i) => {
        p.index = i;
      });
      return doc;
    });
    return copy;
  },

  movePage: (projectId, pageId, toIndex) =>
    get().applyDocMutation(projectId, (doc) => {
      const from = doc.pages.findIndex((p) => p.id === pageId);
      if (from < 0) throw new Error(`Unknown page "${pageId}"`);
      const clamped = Math.max(0, Math.min(doc.pages.length - 1, Math.round(toIndex)));
      const [page] = doc.pages.splice(from, 1);
      if (!page) return doc;
      doc.pages.splice(clamped, 0, page);
      doc.pages.forEach((p, i) => {
        p.index = i;
      });
      return doc;
    }),

  ensureCoverPage: (projectId, reviewerInitials) => {
    const deck = get().decks[projectId];
    if (!deck || deck.doc.pages.some(p => p.cover)) return;
    const ctx = blockContext(deck, get().runtime[projectId]?.reviewLanguage, reviewerInitials);
    get().applyDocMutation(projectId, (doc) => {
      const elements = (['betterPlaces', 'tenantSurvey', 'cartouche'] as const).map((kind): SlideElement => ({
        id: slidesGenId(), type: 'block', block: kind, fields: BLOCK_LIBRARY[kind].defaults(ctx), frame: { ...COVER_LAYOUT[kind] }, origin: 'user',
      }));
      doc.pages.unshift({ id: slidesGenId(), index: 0, kind: 'blank', cover: true, label: 'Review cover', ...COVER_PAGE, elements });
      doc.pages.forEach((p, i) => { p.index = i; });
      const review = doc.review ?? { guidelineKey: (deck.guidelineKey ?? 'inline_unit') as SlidesGuidelineKey, round: deck.reviewRound ?? 1, points: {} };
      doc.review = { ...review, stamp: { ...COVER_LAYOUT.stamp, fontScale: review.stamp?.fontScale ?? 1 },
        header: { ...review.header, rdm: review.header?.rdm?.trim() || ctx.header?.rdm || undefined } };
      return doc;
    });
  },

  addLibraryStamp: (projectId, pageId, kind, reviewerInitials) => {
    const deck = get().decks[projectId];
    const page = deck?.doc.pages.find(p => p.id === pageId);
    if (!deck || !page) return;
    const k = Math.min(1, page.width / COVER_PAGE.width);
    const { w, h } = BLOCK_LIBRARY[kind].size;
    const slot = page.cover && kind in COVER_LAYOUT && !page.elements.some(el => el.type === 'block' && el.block === kind) ? COVER_LAYOUT[kind as keyof typeof COVER_LAYOUT] : null;
    const element: SlideElement = { id: slidesGenId(), type: 'block', block: kind, fields: BLOCK_LIBRARY[kind].defaults(blockContext(deck, get().runtime[projectId]?.reviewLanguage, reviewerInitials)), origin: 'user',
      frame: slot ? { ...slot } : { x: (page.width - w * k) / 2, y: (page.height - h * k) / 2, w: w * k, h: h * k } };
    get().addElement(projectId, pageId, element);
    get().patchRuntime(projectId, { selectedElementIds: [element.id], activeTool: 'select' });
  },

  addPage: (projectId, page, atIndex) => {
    const deck = get().decks[projectId];
    if (!deck) return null;
    const newPage: SlidePageDoc = {
      id: slidesGenId(),
      index: deck.doc.pages.length,
      kind: 'blank',
      width: 960,
      height: 540,
      elements: [],
      ...page,
    };
    get().applyDocMutation(projectId, (doc) => {
      const at =
        typeof atIndex === 'number' && Number.isFinite(atIndex)
          ? Math.max(0, Math.min(doc.pages.length, Math.round(atIndex)))
          : doc.pages.length;
      doc.pages.splice(at, 0, newPage);
      doc.pages.forEach((p, i) => {
        p.index = i;
      });
      return doc;
    });
    return newPage;
  },

  deletePage: (projectId, pageId) => {
    const deck = get().decks[projectId];
    if (!deck) return;
    // A deck always keeps at least one page.
    if (deck.doc.pages.length <= 1) return;
    const removedIndex = deck.doc.pages.findIndex((p) => p.id === pageId);
    if (removedIndex < 0) return;
    get().applyDocMutation(projectId, (doc) => {
      doc.pages = doc.pages.filter((p) => p.id !== pageId);
      doc.pages.forEach((p, i) => {
        p.index = i;
      });
      return doc;
    });
    if (get().runtime[projectId]?.activePageId === pageId) {
      const nextPages = get().decks[projectId]?.doc.pages ?? [];
      const neighbour = nextPages[Math.min(removedIndex, nextPages.length - 1)];
      if (neighbour) get().focusPage(projectId, neighbour.id);
    }
  },

  setReview: (projectId, patch, coalesceKey) =>
    get().applyDocMutation(projectId, (doc) => {
      const deck = get().decks[projectId];
      const prev: DeckReviewState = doc.review ?? {
        guidelineKey: (deck?.guidelineKey ?? 'inline_unit') as SlidesGuidelineKey,
        round: deck?.reviewRound ?? 1,
        points: {},
        header: { date: new Date().toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: '2-digit', timeZone: 'UTC' }) },
      };
      doc.review = {
        ...prev,
        ...patch,
        ...(patch.header ? { header: { ...prev.header, ...patch.header } } : {}),
        points: { ...prev.points, ...(patch.points ?? {}) },
      };
      return doc;
    }, coalesceKey),

  // The card and the right bar change the two stamp lines through these, the Reviewer through
  // setReview: the authority line is its own judgement (URW), so a person's verdict change leaves
  // it as shown, and its printed note belongs to the colour it was written for.
  setReviewStatus: (projectId, status) => {
    const review = get().decks[projectId]?.doc.review;
    get().setReview(projectId, review && review.authorityStatus === undefined
      ? { status, statusText: undefined, authorityStatus: resolveAuthorityStatus(undefined, review.status ?? 'C') } : { status, statusText: undefined });
  },
  toggleAuthorityStatus: (projectId) => {
    const review = get().decks[projectId]?.doc.review;
    if (!review) return;
    const shown = resolveAuthorityStatus(review.authorityStatus, review.status ?? 'C');
    get().setReview(projectId, { authorityStatus: shown === 'pass' ? 'fail' : 'pass', daNote: undefined, authorityText: undefined });
  },

  clearReview: (projectId) => {
    const deck = get().decks[projectId];
    if (!deck) return 0;
    const REVIEW_TYPES = REVIEW_PLACED_TYPES;
    let removed = 0;
    get().applyDocMutation(projectId, (doc) => {
      doc.pages = doc.pages.map((page) => {
        const keep = page.elements.filter((el) => !REVIEW_TYPES.has(el.type));
        removed += page.elements.length - keep.length;
        return keep.length === page.elements.length ? page : { ...page, elements: keep };
      });
      // Keep the guideline and round — the deck is still the same submission on
      // the same guidelines; only the findings are gone. The stamp's placement
      // is dropped with the rest so a fresh review lays it out again.
      doc.review = doc.review
        ? { guidelineKey: doc.review.guidelineKey, round: doc.review.round, points: {} }
        : doc.review;
      delete doc.reviewRun;
      return doc;
    });
    set((s) => {
      const cleared = s.decks[projectId];
      return cleared ? {
        decks: { ...s.decks, [projectId]: { ...cleared, status: 'DRAFT' } },
        runtime: { ...s.runtime, [projectId]: { ...s.runtime[projectId], reviewRunId: null } },
      } : {};
    });
    console.info(`[Slides] clearReview: removed ${removed} review element(s) from ${deck.doc.pages.length} page(s)`);
    return removed;
  },

  setDeckMeta: (projectId, patch) => {
    const deck = get().decks[projectId];
    if (!deck) return;
    const run = deck.doc.reviewRun;
    const roundChanged = patch.reviewRound !== undefined && patch.reviewRound !== deck.reviewRound;
    const guidelineChanged = patch.guidelineKey !== undefined && patch.guidelineKey !== deck.guidelineKey;
    if (run?.evidenceVersion === 1 && run.status === 'running' && (roundChanged || guidelineChanged)) {
      throw new Error('Stop the current review before changing its round or guidelines.');
    }
    const doc = roundChanged
      ? { ...deck.doc, reviewRun: undefined, proposalRun: undefined }
      : run?.status === 'running' && agentMutationDepth === 0
        ? { ...deck.doc, reviewRun: { ...run, userEdited: true } } : deck.doc;
    set((s) => ({
      decks: { ...s.decks, [projectId]: { ...deck, ...patch, doc, version: deck.version + 1 } },
    }));
    queueSave(projectId);
  },

  patchRuntime: (projectId, patch) =>
    set((s) => ({
      runtime: { ...s.runtime, [projectId]: { ...s.runtime[projectId], ...patch } },
    })),

  focusPage: (projectId, pageId) =>
    set((s) => ({
      runtime: { ...s.runtime, [projectId]: { ...s.runtime[projectId], activePageId: pageId } },
    })),

  requestVision: (pageId) =>
    set((s) => ({
      visionRequestPageIds: s.visionRequestPageIds.includes(pageId)
        ? s.visionRequestPageIds
        : [...s.visionRequestPageIds, pageId],
    })),

  consumeVisionRequests: () => {
    const ids = get().visionRequestPageIds;
    if (ids.length > 0) set({ visionRequestPageIds: [] });
    return ids;
  },

  // Every agent command calls this before it touches anything (requireDeck in
  // slidesCommands.ts), which makes it the natural place to open an undo step:
  // one command, one Ctrl+Z, however many elements it went on to place. Without
  // it a twenty-callout review is twenty presses AND evicts the user's own
  // history from a bounded stack.
  markAgentActive: () => set({ agentActiveAt: Date.now(), agentStepKey: `agent:${Date.now()}` }),
  setAgentEdits: (e) => set({ agentEdits: e }),

  startAgentTyping: (elementId) =>
    set((s) => {
      if (s.agentTyping.order.includes(elementId)) return {};
      return {
        agentTyping: {
          order: [...s.agentTyping.order, elementId],
          shown: { ...s.agentTyping.shown, [elementId]: 0 },
        },
      };
    }),

  advanceAgentTyping: (elementId, chars, done) =>
    set((s) => {
      if (!(elementId in s.agentTyping.shown)) return {};
      if (done) {
        // Drop it entirely rather than leaving a completed entry behind: the
        // canvas treats "not in the map" as "render normally", so finishing is
        // the same thing as handing the element back to the normal path.
        const shown = { ...s.agentTyping.shown };
        delete shown[elementId];
        return {
          agentTyping: { order: s.agentTyping.order.filter((id) => id !== elementId), shown },
        };
      }
      return {
        agentTyping: {
          ...s.agentTyping,
          shown: { ...s.agentTyping.shown, [elementId]: chars },
        },
      };
    }),

  clearAgentTyping: () =>
    set((s) => (s.agentTyping.order.length === 0 ? {} : { agentTyping: { order: [], shown: {} } })),

  waitForAgentTyping: (timeoutMs = 180000) =>
    new Promise<void>((resolve) => {
      if (get().agentTyping.order.length === 0) return resolve();
      // Nothing drives the animation except a mounted canvas (SlidesCanvas is
      // the only caller of advanceAgentTyping). Headless runs have none, and
      // SLIDES_ADD_ELEMENT is headlessCompatible, so without this every
      // annotate command paid the stall watchdog before returning.
      if (typeof document === 'undefined') {
        useSlidesStore.getState().clearAgentTyping();
        return resolve();
      }
      let done = false;
      const startedAt = Date.now();
      const finish = () => {
        if (done) return;
        done = true;
        unsubscribe();
        clearInterval(watchdog);
        resolve();
      };
      // Two independent ways out. STALL: nothing written for a few seconds means
      // nothing is going to be. CAP: an absolute ceiling measured from the START,
      // not from the last change — comparing the cap against lastChange made it
      // dead code, since the stall threshold always tripped first.
      let lastChange = Date.now();
      let lastSeen = JSON.stringify(get().agentTyping.shown);
      const watchdog = setInterval(() => {
        const now = JSON.stringify(useSlidesStore.getState().agentTyping.shown);
        if (now !== lastSeen) {
          lastSeen = now;
          lastChange = Date.now();
          return;
        }
        if (Date.now() - lastChange > TYPING_STALL_MS || Date.now() - startedAt > timeoutMs) {
          useSlidesStore.getState().clearAgentTyping();
          finish();
        }
      }, 500);
      const unsubscribe = useSlidesStore.subscribe((state) => {
        if (state.agentTyping.order.length === 0) finish();
      });
    }),

  setReadingPage: (pageId) => set((s) => (s.readingPageId === pageId ? {} : { readingPageId: pageId })),

  clearAgentEdits: () => set((s) => (s.agentEdits ? { agentEdits: null } : {})),
  setAutoFollowAgent: (on) => set((s) => (s.autoFollowAgent === on ? {} : { autoFollowAgent: on })),
  };
});

/** Resolve the deck the agent operates on: explicit projectId, else the open deck. */
export const resolveAgentSlideDeck = (projectId?: string | null): SlideDeckDTO | null => {
  const st = useSlidesStore.getState();
  const pid = projectId ?? st.activeProjectId;
  return pid ? st.decks[pid] ?? null : null;
};

/** Called where a session's turn or script chain ends. The stop flag is read before the research run
 *  closes, because closing it fires abort listeners (kernel/useCommands.ts) that raise that flag. */
export function startReviewAfterResearch(
  k: (Pick<useAgentsReturnType, 'askAssistant'> & {
    logUserAction?: (event: string, payload?: Record<string, unknown>) => void;
    readEntityRuntimeState: (type: 'ChatSession', id: string) => { isStopRequested?: boolean };
  }) | null | undefined,
  sessionId: string,
): void {
  if (!k) return;
  const stopped = !!k.readEntityRuntimeState('ChatSession', sessionId).isStopRequested;
  const thenReview = useSlidesStore.getState().takeReviewAfterResearch(sessionId);
  if (thenReview && !stopped) { setTimeout(() => startSlidesReview(k, sessionId, thenReview), 0); return; }
  const asked = useSlidesStore.getState().pendingReviewLaunch;
  if (!asked || asked.sessionId !== sessionId) return;
  useSlidesStore.setState({ pendingReviewLaunch: null });
  const deck = useSlidesStore.getState().decks[asked.projectId];
  if (stopped || !deck || asked.projectId !== useSlidesStore.getState().activeProjectId) return;
  const progress = reviewProgress(deck).percent;
  setTimeout(() => launchSlidesReview(k, sessionId, progress > 0 ? 'resume' : 'start'), 0);
}

/** A typed "start / continue the review" presses the button (owner, 2026-10-09): true when it launched.
 *  Launch only at zero, Continue for anything started; a finished or running review is left to the agent. */
export function launchTypedSlidesReview(
  k: Parameters<typeof launchSlidesReview>[0],
  sessionId: string,
  message: string,
): boolean {
  const intent = typedReviewIntent(message);
  const st = useSlidesStore.getState();
  const deck = st.activeProjectId ? st.decks[st.activeProjectId] : undefined;
  if (!intent || !deck || st.getAgentRun(sessionId) || reviewHasCompleteResult(deck)) return false;
  const started = reviewProgress(deck).percent > 0;
  if (intent === 'continue' && !started) return false;
  launchSlidesReview(k, sessionId, started ? 'resume' : 'start');
  k.logUserAction?.('Slides Review Launched From Chat', { projectId: deck.projectId, intent, resumed: started });
  return true;
}

/** Every review starts here, from the button or from a chat request: a new review researches the brand
 *  first when it has no brief; Continue keeps its language and what is already done. */
export function launchSlidesReview(
  k: Pick<useAgentsReturnType, 'askAssistant'> & {
    logUserAction?: (event: string, payload?: Record<string, unknown>) => void;
    getUserPreferences?: () => { language?: 'ENGLISH' | 'FRENCH' | null } | null | undefined;
    getUserContext?: () => { impersonatedUser?: { name: string } | null; trueUser?: { name: string } | null };
  },
  sessionId: string,
  mode: 'start' | 'restart' | 'resume',
): void {
  const store = useSlidesStore.getState();
  const deck = store.activeProjectId ? store.decks[store.activeProjectId] : undefined;
  if (!deck) return;
  if (mode === 'resume') { startSlidesReview(k, sessionId, 'start', resumeReviewPrompt(deck)); return; }
  // Picked for this deck, else the review's own, else the user's saved language; none leaves it to the dossier.
  const language = store.runtime[deck.projectId]?.reviewLanguage ?? deck.doc.review?.language
    ?? reviewLanguageFromPreference(k.getUserPreferences?.()?.language);
  if (language) store.patchRuntime(deck.projectId, { reviewLanguage: language });
  // A research run still open here never finished: the review goes ahead without it.
  if (!store.brandResearchRun && needsBrandResearch(deck.doc.brandResearch)) {
    store.beginBrandResearch(deck.projectId, sessionId, mode);
    void k.askAssistant(sessionId, BRAND_RESEARCH_PROMPT, undefined, undefined, { agentMode: 'slides', skipUserMessage: true, showThinking: true });
    k.logUserAction?.('Slides Brand Research Requested', { projectId: deck.projectId, beforeReview: true });
    return;
  }
  startSlidesReview(k, sessionId, mode);
}

/** The one way a review run starts: the launcher's buttons and the review that follows brand research. */
export function startSlidesReview(
  k: Pick<useAgentsReturnType, 'askAssistant'> & {
    logUserAction?: (event: string, payload?: Record<string, unknown>) => void;
    getUserContext?: () => { impersonatedUser?: { name: string } | null; trueUser?: { name: string } | null };
  },
  sessionId: string,
  slidesReview: 'start' | 'restart',
  prompt = REVIEW_PROMPT,
): void {
  const st = useSlidesStore.getState();
  st.cancelBrandResearch();
  const who = k.getUserContext?.();
  if (st.activeProjectId) st.ensureCoverPage(st.activeProjectId, initialsOf(who?.impersonatedUser?.name ?? who?.trueUser?.name));
  void k.askAssistant(sessionId, prompt, undefined, undefined, { agentMode: 'slides', skipUserMessage: true, showThinking: true, slidesReview });
  const deck = st.activeProjectId ? st.decks[st.activeProjectId] : undefined;
  k.logUserAction?.('Slides Review Launched', { guideline: deck?.guidelineKey ?? null, resumed: prompt !== REVIEW_PROMPT, restart: slidesReview === 'restart' });
}

// Dev-only handle for the headless E2E harness (e2e-slides.mjs), which drives
// the real editor and asserts against live store state rather than scraping
// the DOM. Never attached in a production build.
if (typeof window !== 'undefined' && process.env.NODE_ENV !== 'production') {
  (window as unknown as { __slidesStore?: typeof useSlidesStore }).__slidesStore = useSlidesStore;
}
